import React, { useState } from "react"
import { MentionsInput, Mention } from "react-mentions"

const DELAY_UNITS = [
  { value: "hour", label: "Hour" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
]

const detectBodyVarCount = tpl => {
  const body = (tpl?.components || []).find(
    c => String(c.type || "").toUpperCase() === "BODY"
  )
  const text = body?.text || tpl?.body_preview || ""
  const matches = String(text).match(/\{\{(\d+)\}\}/g) || []
  if (matches.length === 0) return 0
  return new Set(matches.map(m => m.replace(/[{}]/g, ""))).size
}

const detectHeaderMediaType = tpl => {
  const header = (tpl?.components || []).find(
    c => String(c.type || "").toUpperCase() === "HEADER"
  )
  const format = String(header?.format || "").toUpperCase()
  if (["IMAGE", "VIDEO", "DOCUMENT"].includes(format)) {
    return format.toLowerCase()
  }
  return ""
}

const Messages = props => {
  const [state, setState] = useState({
    name: "React",
    value: props.data?.message ? props.data?.message : "",
    singleLineValue: props.data?.message ? props.data?.message : "",
    mentionData: null,
    variables: props.variables || [],
  })

  const variables = props.variables || []
  const templateKey = props.data?.template_name
    ? `${props.data.template_name}||${props.data.language_code || "en_US"}`
    : ""
  const selectedTemplate =
    (props.metaTemplates || []).find(
      t => `${t.name}||${t.language}` === templateKey
    ) || null

  const handleChangeSingle = (e, newValue) => {
    setState({
      ...state,
      singleLineValue: newValue,
    })
    props.handleFields(
      props.index,
      { target: { name: "", value: newValue } },
      props.id
    )
  }

  const applyTemplate = key => {
    const tpl =
      (props.metaTemplates || []).find(
        t => `${t.name}||${t.language}` === key
      ) || null
    if (!tpl) {
      props.onPatch({
        wa_mode: "meta_template",
        template_name: "",
        language_code: "en_US",
        body_param_vars: [],
        header_type: "",
        header_link_var: "",
      })
      return
    }
    const count = detectBodyVarCount(tpl)
    const media = detectHeaderMediaType(tpl)
    props.onPatch({
      wa_mode: "meta_template",
      template_name: tpl.name,
      language_code: tpl.language || "en_US",
      body_param_vars: Array(count).fill(""),
      header_type: media || "",
      header_link_var: "",
      message: "",
    })
  }

  const bodyParamVars = Array.isArray(props.data?.body_param_vars)
    ? props.data.body_param_vars
    : []

  return (
    <div className="row" key={props.i}>
      <div className="col-lg-7 mb-3 required">
        {props.metaEnabled ? (
          <>
            {props.loadingMetaTemplates ? (
              <div className="small text-muted">Loading templates…</div>
            ) : (props.metaTemplates || []).length === 0 ? (
              <div className="alert alert-warning py-2 mb-0 small">
                No approved Meta templates found. Sync templates from WhatsApp
                Business settings first.
              </div>
            ) : (
              <select
                className="form-select"
                value={templateKey}
                onChange={e => applyTemplate(e.target.value)}
              >
                <option value="">Select template…</option>
                {(props.metaTemplates || []).map(t => (
                  <option
                    key={`${t.id}-${t.name}-${t.language}`}
                    value={`${t.name}||${t.language}`}
                  >
                    {t.name} ({t.language})
                    {detectBodyVarCount(t) > 0
                      ? ` · ${detectBodyVarCount(t)} vars`
                      : ""}
                  </option>
                ))}
              </select>
            )}
            {selectedTemplate?.body_preview ? (
              <p className="small text-muted mt-2 mb-0">
                Preview: {selectedTemplate.body_preview}
              </p>
            ) : null}
            {bodyParamVars.length > 0 ? (
              <div className="row g-2 mt-2">
                {bodyParamVars.map((val, idx) => (
                  <div className="col-md-6" key={idx}>
                    <label className="form-label small mb-1">
                      Variable {"{{"}
                      {idx + 1}
                      {"}}"}
                    </label>
                    <select
                      className="form-select form-select-sm mb-1"
                      value={
                        variables.some(v => v._id === val)
                          ? val
                          : val
                          ? "__custom__"
                          : ""
                      }
                      onChange={e => {
                        const next = [...bodyParamVars]
                        next[idx] =
                          e.target.value === "__custom__" ? "" : e.target.value
                        props.onPatch({ body_param_vars: next })
                      }}
                    >
                      <option value="">Select variable…</option>
                      {variables.map(v => (
                        <option key={v._id} value={v._id}>
                          {v.name}
                        </option>
                      ))}
                      <option value="__custom__">Custom text…</option>
                    </select>
                    {!variables.some(v => v._id === val) && val !== undefined ? (
                      <input
                        className="form-control form-control-sm"
                        placeholder="Custom value or {lead_name}"
                        value={val}
                        onChange={e => {
                          const next = [...bodyParamVars]
                          next[idx] = e.target.value
                          props.onPatch({ body_param_vars: next })
                        }}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
            {props.data?.header_type ? (
              <div className="mt-2">
                <label className="form-label small mb-1">
                  Header {props.data.header_type} public URL
                </label>
                <input
                  className="form-control form-control-sm"
                  placeholder="https://… public media URL"
                  value={props.data.header_link_var || ""}
                  onChange={e =>
                    props.onPatch({
                      header_type: props.data.header_type,
                      header_link_var: e.target.value,
                    })
                  }
                />
              </div>
            ) : null}
          </>
        ) : (
          <>
            <MentionsInput
              id={`message-${props.i + 1}`}
              value={state.singleLineValue}
              onChange={handleChangeSingle}
              markup="@{{__type__||__id__||__display__}}"
              placeholder="use the @ symbol to select variables."
              className="mentions"
              required
            >
              <Mention
                type="user"
                trigger="@"
                data={props?.variableData}
                className="mentions__mention"
              />
            </MentionsInput>
            <small className="fst-italic">{`Use @ to select variables to create message.`}</small>
          </>
        )}
      </div>
      <div className="col-lg-5 mb-3 required d-flex justify-content-between align-items-center gap-2">
        <input
          style={{ width: "42%" }}
          type="number"
          min="1"
          className="form-control"
          id={`hours-${props.i + 1}`}
          name="hours"
          placeholder="Number"
          onChange={e => props.handleFields(props.index, e, props.id)}
          required
          defaultValue={props.data?.hours}
        />
        <select
          className="form-select"
          style={{ width: "calc(58% - 48px)" }}
          name="delay_unit"
          value={props.data?.delay_unit || "hour"}
          onChange={e => props.handleFields(props.index, e, props.id)}
        >
          {DELAY_UNITS.map(unit => (
            <option key={unit.value} value={unit.value}>
              {unit.label}
            </option>
          ))}
        </select>
        {props.allowRemoveFirst || props.index !== 0 ? (
          <span
            className="btn btn-danger"
            onClick={() => props.removeFields(props.index, props.id)}
          >
            &times;
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default Messages
